import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import ENVIRONMENTS, {
  QC_AFTER_SSO_LOGOUT_KEY,
  QC_SSO_CLEARED_KEY,
  getKeycloakRedirectUri,
  isConfiguredKeycloakUrl,
} from '../../../environments';
import { RootState } from '../../../store/create-store';
import { registrationUrl } from '../../premium-profile/quicklinks-attribution';
import { UserService } from '../../../common/services/user/user.service';
import { IReferrerData } from '../../../common/services/user/user-data.interface';
import { useAppData } from '../../../common/contexts/app-data.context';
import { apiV2Instance } from '../../../common/lib/api-v2.client';
import { isDevAuthBypass } from '../../../common/dev/dev-auth-bypass';
import useAlert from '../../../common/hooks/useAlert';
import { AlertVariant } from '../../../common/providers/alert.provider';
import {
  buildOnboardingLoginAlertMessage,
  consumeOnboardingLoginAlert,
} from '../../../common/lib/onboarding-login-alert';
import CinematicHomepage from './components/cinematic-homepage.component';
import {
  applyGiftLanding,
  GiftInvitePreview,
  LA_PASS_GIFTCARD_KEY,
  QUICK_LINKS_HOLD_KEY,
  QUICK_LINKS_HOLD_REQUEST_KEY,
  claimStoredGiftSignup,
  resumeStoredCampaign,
} from '../../lifetime-credits/gift-invite-welcome';
import { bindQuickLinksHold } from '../../lifetime-credits/quick-links-hold';
import { referringChannelRequest } from './referring-channel-request';

const userServices = new UserService();

function Login(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const commonState = useSelector((state: RootState) => state.common);
  const { auth, user } = commonState;
  const isLoggedIn = Boolean(auth) && Boolean(user);

  const [referredBy, setReferredBy] = useState<IReferrerData>();
  const [isLoadingReferrer, setIsLoadingReferrer] = useState(false);
  const [giftInvite, setGiftInvite] = useState<GiftInvitePreview | null>(null);
  const { params: pathParams }: { params: any } = match;
  const { setServiceAvailable, serviceAvailable } = useAppData();
  const { showAlert } = useAlert();

  useEffect(() => {
    const pending = consumeOnboardingLoginAlert();
    if (pending) {
      showAlert(buildOnboardingLoginAlertMessage(pending), AlertVariant.ERROR);
    }
  }, [showAlert]);

  const getReferrerUser = async (
    channelId?: string,
    startTimer?: boolean,
  ) => {
    const queryParams = new URLSearchParams(location.search);
    const request = channelId
      ? { kind: 'visit' as const, channelId }
      : referringChannelRequest({
        queryChannel: queryParams.get('referringchannel'),
        pathChannel: pathParams.referrerChannelId,
      });

    setIsLoadingReferrer(true);

    const visitParams = {
      params: {
        refCode: queryParams.get('refCode') || null,
        startTimer: startTimer || null,
      },
    };
    const visitRequest = request.kind === 'visit'
      ? apiV2Instance.get(`/api/user/visit/${request.channelId}`, visitParams)
      : apiV2Instance.get('/api/user/default-referral', visitParams);

    await visitRequest
      .then((referrer) => {
        const visit = referrer.data || {};
        setServiceAvailable(
          visit.serviceAvailabilityStatus !== 'NOT_AVAILABLE',
        );
        setReferredBy({
          ...visit,
          publicDisplayName:
            visit.publicDisplayName ||
            visit.displayName ||
            visit.channelName ||
            visit.username,
        });
        if (request.kind === 'default') {
          const capturedChannelId = localStorage
            .getItem('referringChannelId')
            ?.trim();
          const resolvedChannelId = String(
            visit.channelId || visit.channelName || '',
          ).trim();
          if (!capturedChannelId && resolvedChannelId) {
            localStorage.setItem('referringChannelId', resolvedChannelId);
          }
        }
      })
      .catch(async (error) => {
        console.error(error);
        if (auth) {
          const defaultReferrer = await userServices.getDefaultChannels();
          const firstItem = Array.isArray(defaultReferrer)
            ? defaultReferrer[0]
            : undefined;
          if (firstItem?.channelId) {
            localStorage.setItem('referringChannelId', firstItem.channelId);
          }
        }
        setServiceAvailable(serviceAvailable);
      })
      .finally(() => {
        setIsLoadingReferrer(false);
      });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const giftcard = queryParams.has('giftcard') ? (queryParams.get('giftcard') || '') : null;
    const nextGift = applyGiftLanding({
      token: localStorage.getItem(LA_PASS_GIFTCARD_KEY) || '',
      holdKey: localStorage.getItem(QUICK_LINKS_HOLD_KEY) || '',
      requestId: localStorage.getItem(QUICK_LINKS_HOLD_REQUEST_KEY) || '',
    }, giftcard);
    if (nextGift.token) localStorage.setItem(LA_PASS_GIFTCARD_KEY, nextGift.token);
    else localStorage.removeItem(LA_PASS_GIFTCARD_KEY);
    if (nextGift.holdKey) localStorage.setItem(QUICK_LINKS_HOLD_KEY, nextGift.holdKey);
    else localStorage.removeItem(QUICK_LINKS_HOLD_KEY);
    if (nextGift.requestId) localStorage.setItem(QUICK_LINKS_HOLD_REQUEST_KEY, nextGift.requestId);
    else localStorage.removeItem(QUICK_LINKS_HOLD_REQUEST_KEY);
    if (nextGift.clearInvite) setGiftInvite(null);
    if (giftcard) {
      apiV2Instance
        .get(`/api/lifetime-access-credits/invites/token/${encodeURIComponent(giftcard)}`)
        .then((preview) => {
          const scope = preview.data?.scope === 'DUAL' ? 'DUAL' : 'SINGLE';
          const quantity = Number(preview.data?.quantity || preview.data?.reserved) || 1;
          const contactName = String(preview.data?.contactName || '').trim();
          const message = preview.data?.kind === 'campaign'
            ? String(preview.data?.welcome || '')
            : '';
          setGiftInvite({
            scope,
            quantity,
            contactName,
            ...(message ? { message } : {}),
          });
        })
        .catch(() => setGiftInvite(null));
    }
    const referringChannel = queryParams.get('referringchannel');
    const request = referringChannelRequest({
      queryChannel: referringChannel,
      pathChannel: pathParams.referrerChannelId,
    });

    if (request.kind === 'visit' && !referredBy) {
      localStorage.setItem('referringChannelId', request.channelId);
      getReferrerUser(request.channelId);
    } else if (request.kind === 'default' && !referredBy) {
      // A bare `/` displays the configured default referring channel.
      // Do not overwrite a channel already captured from `/:referrerChannelId`:
      // the post-signup Keycloak callback returns to `/`, and onboarding reads
      // this value to pre-select the actual referrer (e.g. "sachin").
      getReferrerUser();
    }
  }, [pathParams.referrerChannelId, location.search, referredBy]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    resumeStoredCampaign({
      read: (token) => apiV2Instance
        .get(`/api/lifetime-access-credits/invites/token/${encodeURIComponent(token)}`)
        .then((res) => res.data),
      bind: bindQuickLinksHold,
      join: (token) => apiV2Instance
        .post(`/api/lifetime-access-credits/campaigns/token/${encodeURIComponent(token)}/participate`)
        .then((res) => {
          const scope = res.data?.scope === 'DUAL' ? 'DUAL' : 'SINGLE';
          const quantity = Number(res.data?.reserved) || 1;
          const message = String(res.data?.welcome || '');
          setGiftInvite({
            scope,
            quantity,
            ...(message ? { message } : {}),
          });
          return res.data;
        }),
    }).then((joined) => {
      if (joined) return undefined;
      return claimStoredGiftSignup((token, origin) => (
        apiV2Instance.post(
          `/api/lifetime-access-credits/invites/token/${encodeURIComponent(token)}/signup`,
          { origin },
        ).then((res) => res.data)
      ));
    }).catch(() => undefined);
    return undefined;
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;
    let pending = '';
    try {
      pending = sessionStorage.getItem(QC_AFTER_SSO_LOGOUT_KEY) || '';
    } catch {
      return;
    }
    if (pending !== 'signup') return;
    sessionStorage.removeItem(QC_AFTER_SSO_LOGOUT_KEY);
    window.location.href = registrationUrl(getKeycloakRedirectUri());
  }, [isLoggedIn]);

  const redirectToSignUp = () => {
    if (!isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_ACCOUNT)) {
      console.error(
        'Keycloak is not configured. Run npm run env:local and restart the dev server.',
      );
      return;
    }

    window.localStorage.removeItem('redirect_uri');
    try {
      sessionStorage.removeItem(QC_SSO_CLEARED_KEY);
      sessionStorage.removeItem(QC_AFTER_SSO_LOGOUT_KEY);
    } catch {
      /* ignore */
    }
    window.location.href = registrationUrl(getKeycloakRedirectUri());
  };

  const redirectToLogin = () => {
    if (isDevAuthBypass) {
      history.push('/u/dashboard');
      return;
    }
    if (!isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_ACCOUNT)) {
      console.error(
        'Keycloak is not configured. Run npm run env:local and restart the dev server.',
      );
      return;
    }
    const redirectUri = getKeycloakRedirectUri();
    window.localStorage.removeItem('redirect_uri');

    const loginUrl = `${ENVIRONMENTS.KEYCLOAK_ACCOUNT}/protocol/openid-connect/auth?client_id=${ENVIRONMENTS.KEYCLOAK_CLIENT_ID}&response_mode=fragment&response_type=code&scope=${encodeURIComponent('openid email profile')}&login=true&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&from=Login`;
    window.location.href = loginUrl;
  };

  return (
    <CinematicHomepage
      isLoggedIn={isLoggedIn}
      referredBy={referredBy}
      isLoadingReferrer={isLoadingReferrer}
      getReferrerUser={getReferrerUser}
      onTryQuickCam={redirectToSignUp}
      onLogin={redirectToLogin}
      onDashboard={() => history.push('/u/dashboard')}
      giftInvite={giftInvite}
    />
  );
}

export default Login;
